/**
 * "Lite" mode for decorative effects on phones.
 *
 * iOS Safari has a hard compositor memory budget and rasterises CSS `filter`
 * on the CPU-side of the pipeline. The public pages stack several full-screen
 * blurred layers (`blur(140px)` blobs, a rotating `blur(40px)` gradient),
 * a `hue-rotate` filter over that whole group, `backdrop-filter` panels and
 * three permanent 2D-canvas loops. Each alone is fine; together an iPhone
 * runs out of GPU memory and the page freezes.
 *
 * On phones we keep the look (soft radial gradients, same colours) but drop
 * the layers that cost the most. Desktop and tablets are untouched.
 *
 * Usage:
 *   - `isFxLite()` — cheap, cached; use it in render / effect code.
 *   - `applyFxLiteClass()` — adds `fx-lite` to <html> so CSS can adapt
 *     (`html.fx-lite .landing-page …`). Call it where `landing-active` /
 *     `kb-active` is set.
 */

export const FX_LITE_CLASS = 'fx-lite';

function detectPhone(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPod|Windows Phone/i.test(ua)) return true;
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return true;
  // Unknown UA: a touch device whose shorter screen side is phone-sized.
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const touch = (navigator.maxTouchPoints || 0) > 0 || coarse;
  const minSide = Math.min(window.screen?.width || 0, window.screen?.height || 0);
  return touch && minSide > 0 && minSide <= 480;
}

let cached: boolean | null = null;

/** True on phones (iPhone / Android phone / small touch screens). Cached. */
export function isFxLite(): boolean {
  if (cached === null) cached = detectPhone();
  return cached;
}

/** Toggles the `fx-lite` class on <html>. Returns the flag. */
export function applyFxLiteClass(): boolean {
  const lite = isFxLite();
  document.documentElement.classList.toggle(FX_LITE_CLASS, lite);
  return lite;
}

export function removeFxLiteClass(): void {
  document.documentElement.classList.remove(FX_LITE_CLASS);
}
